<template>
  <div
    class="fixed z-[60] flex flex-col gap-2 p-4 pointer-events-none max-w-md"
    :class="positionClasses"
    data-test-id="global-notifications"
  >
    <AM3Snackbar
      v-for="snackbar in snackbars"
      :key="snackbar.id"
      :message="snackbar.message"
      :title="snackbar.title"
      :type="snackbar.type"
      :visible="snackbar.visible"
      :dismissible="snackbar.dismissible"
      class="pointer-events-auto"
      @close="close(snackbar.id)"
    >
      <template
        v-if="snackbar.action"
        #action
      >
        <button
          class="ml-2 px-2 py-1 text-xs font-medium uppercase tracking-wider text-white hover:bg-white/20 rounded"
          @click="() => handleAction(snackbar)"
        >
          {{ snackbar.action.label }}
        </button>
      </template>
    </AM3Snackbar>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import AM3Snackbar from './AM3Snackbar.vue'
import snackbarService, { type SnackbarItem } from '@/composables/useSnackbar'

const store = useStore()

const props = defineProps({
  position: {
    type: String,
    default: 'bottom-center',
    validator: (value: string) => [
      'top-left', 'top-center', 'top-right',
      'bottom-left', 'bottom-center', 'bottom-right',
    ].includes(value),
  },
})

// Get snackbars from service
const snackbars = computed(() => snackbarService.snackbars.value)

// Get secondary navigation visibility state
const isSecondaryNavigationVisible = computed(() =>
  store?.getters?.['secondaryNavigationModule/isSecondaryNavigationVisible'] ?? false,
)

// Position classes based on position prop
const positionClasses = computed(() => {
  const baseClasses = (() => {
    switch (props.position) {
      case 'top-left':
        return 'top-0 left-0'
      case 'top-center':
        return 'top-0 left-1/2 -translate-x-1/2'
      case 'top-right':
        return 'top-0 right-0'
      case 'bottom-left':
        return 'bottom-0 left-16'
      case 'bottom-center':
        return 'bottom-0 left-1/2 -translate-x-1/2'
      case 'bottom-right':
        return 'bottom-0 right-0'
      default:
        return 'bottom-0 left-1/2 -translate-x-1/2'
    }
  })()

  // Adjust left positioning when secondary navigation is visible
  if (isSecondaryNavigationVisible.value && (isSecondaryNavigationVisible.value && props.position === 'bottom-left')) {
    return baseClasses.replace('left-16', 'md:left-72') // Adjust for secondary nav width
  }

  return baseClasses
})

// Handle action button click
const handleAction = (snackbar: SnackbarItem) => {
  snackbar.action?.callback()
  close(snackbar.id)
}

// Close a snackbar
const close = (id: string) => {
  snackbarService.close(id)
}
</script>
